import React from "react";
import { useTable } from "react-table";
import { TableSkeleton } from "../../../components/loading/tableSkeleton";

// A day column's id is its date (YYYY-MM-DD). Past dates can't take a new shift.
function isDateKeyInPast(dateKey) {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return false;
  const [y, m, d] = dateKey.split("-").map(Number);
  const cellDate = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return cellDate < today;
}

export const ScheduleTable = ({
  data,
  columns,
  isLoading,
  loadingComponent,
  showHeader = true,
  skeletonRows = 5,
}) => {
  const tableInstance = useTable({
    columns,
    data,
  });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
      <table
        {...getTableProps()}
        className="min-w-full text-sm table-fixed border-collapse"
      >
        {showHeader && (
          <thead className="bg-gray-50 dark:bg-gray-800">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, colIndex) => (
                  <th
                    {...column.getHeaderProps()}
                    className={`px-3 py-2 text-center text-xxsm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700 uppercase tracking-wider ${
                      colIndex === 0 ? "w-48" : "w-28 border-l"
                    }`}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {isLoading
            ? Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                <tr key={rowIdx} className="">
                  {columns.map((col, idx) => (
                    <td
                      key={idx}
                      className={`align-top px-2 bg-gray-50 dark:bg-gray-800 ${
                        idx === 0
                          ? "font-medium text-sm border-t border-gray-100 dark:border-gray-700 w-48"
                          : "text-xs text-center w-28 max-w-[7rem] border-t border-l border-gray-100 dark:border-gray-700"
                      }`}
                    >
                      <TableSkeleton />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="">
                    {row.cells.map((cell, idx) => {
                      // Day cells carry the state tint + hover on the <td> and
                      // have NO padding, so the whole cell is tinted *and*
                      // clickable (no dead border zone). `h-px` seeds the cell
                      // height so the inner content can fill it (h-full).
                      const isDayCell = idx !== 0;
                      const dayBg = isDateKeyInPast(cell.column.id)
                        ? "bg-gray-50 dark:bg-gray-800"
                        : "bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-black";
                      return (
                        <td
                          {...cell.getCellProps()}
                          className={`align-top text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700 ${
                            idx === 0
                              ? "pl-1 pt-1 pb-1 font-medium text-sm bg-gray-50 dark:bg-gray-800"
                              : `h-px text-xs text-center w-28 max-w-[7rem] border-l ${dayBg}`
                          }`}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
};
